import bybitWS from "./socketClientBybit";
import {
  IDatafeedChartApi,
  IDatafeedQuotesApi,
  IExternalDatafeed,
  QuotesCallback,
  QuotesErrorCallback,
  ResolutionString,
} from "../../charting_library";
import { TimeSlicesRender } from "../../_app/types";
import { TimeExchangeIntervals, TimeExchangeIntervalsBybit } from "../../_app/consts";

interface Options {
  debug?: boolean;
  ticker: string;
}

interface SymbolInfo {
  symbol: string;
  baseAsset: string;
  quoteAsset: string;
  filters: Array<{ filterType: string; tickSize: string }>;
}

interface Kline {
  [index: number]: number; // Adjust based on actual structure
}

export default class BybitAPI {
  private bybitHost: string;
  private debug: boolean;
  private ws: bybitWS;
  private symbols: any[];
  private ticker: string;

  constructor(options: Options) {
    this.bybitHost = "https://api.bybit.com";
    this.debug = options.debug || false;
    this.ws = new bybitWS();
    this.symbols = [];
    this.ticker = options.ticker;
  }

  // Worked
  async bybitSymbol(): Promise<any> {
    const response = await fetch(
      `${this.bybitHost}/v5/market/instruments-info?category=spot&symbol=${this.ticker}`
    );
    const json = await response.json();
    return json.result.list[0];
  }

  async binanceSymbols(): Promise<SymbolInfo[]> {
    const res = await fetch(`${this.bybitHost}/api/v3/exchangeInfo`);
    const json = await res.json();
    return json.symbols;
  }

  // Worked
  async bybitKlines(
    symbol: string,
    interval: string,
    start?: number,
    end?: number,
    limit?: number
  ) {
    // const url = `${this.bybitHost}/v5/market/kline?symbol=${symbol}&interval=${interval}${limit ? `&limit=${limit}` : ''}`;
    const url = `${this.bybitHost}/v5/market/kline?symbol=${symbol}&interval=${interval}${start ? `&startTime=${start * 1000}` : ""}${end ? `&endTime=${end * 1000}` : ""}`;
    const response = await fetch(url);
    const klines = await response.json();
    return klines.result.list;
  }

  // Worked
  onReady(callback: (data: any) => void): void {
    this.bybitSymbol()
      .then((symbol) => {
        this.symbols = [symbol];
        callback({
          supports_marks: false,
          supports_timescale_marks: false,
          supports_time: true,
          supported_resolutions: [
            "1",
            "3",
            "5",
            "15",
            "30",
            "60",
            "120",
            "240",
            "360",
            "480",
            "720",
            "1D",
            "3D",
            "1W",
            "1M",
          ],
        });
      })
      .catch((err) => {
        console.error(err);
      });
  }

  searchSymbols(
    userInput: string,
    exchange: string,
    symbolType: string,
    onResultReadyCallback: (result: any[]) => void
  ): void {
    // userInput = userInput.toUpperCase();
    // const results = this.symbols
    // 	.filter((symbol) => symbol.symbol.includes(userInput))
    // 	.map((symbol) => ({
    // 		symbol: symbol.symbol,
    // 		full_name: symbol.symbol,
    // 		description: `${symbol.baseAsset} / ${symbol.quoteAsset}`,
    // 		ticker: symbol.symbol,
    // 		exchange: 'Bybit',
    // 		type: 'crypto'
    // 	}));
    //
    // onResultReadyCallback(results);
  }

  resolveSymbol(
    symbolName: string,
    onSymbolResolvedCallback: (data: any) => void,
    onResolveErrorCallback: (errorMsg: string) => void
  ): void {
    // if (this.debug)

    const comps = symbolName.split(":");
    symbolName = (comps.length > 1 ? comps[1] : symbolName).toUpperCase();

    const pricescale = (symbol: any): number => {
      if (symbol.priceFilter.tickSize) {
        return Math.round(1 / parseFloat(symbol.priceFilter.tickSize));
      }
      return 1;
    };

    for (const symbol of this.symbols) {
      if (symbol.symbol === symbolName) {
        setTimeout(() => {
          onSymbolResolvedCallback({
            name: symbol.symbol,
            description: `${symbol.baseCoin} / ${symbol.quoteCoin}`,
            ticker: symbol.symbol,
            exchange: "Bybit",
            listed_exchange: "Bybit",
            type: "crypto",
            session: "24x7",
            minmov: 1,
            pricescale: pricescale(symbol),
            has_intraday: true,
            has_daily: true,
            has_weekly_and_monthly: true,
            currency_code: symbol.quoteCoin,
          });
        }, 0);
        return;
      }
    }

    onResolveErrorCallback("not found");
  }

  // Worked
  async getBars(
    symbolInfo: { name: string },
    resolution: ResolutionString,
    periodParams: { from: number; to: number; firstDataRequest?: boolean },
    onHistoryCallback: (dataArray?: any[], meta?: { noData?: boolean }) => void,
    onErrorCallback: (errorMsg?: string) => void
  ): Promise<void> {
    const { from, to } = periodParams;
    const interval =
      TimeExchangeIntervalsBybit[resolution as keyof typeof TimeExchangeIntervalsBybit];

    if (!interval) {
      onErrorCallback("Invalid interval");
    }

    let totalKlines: any[] = [];
    const kLinesLimit = 500;

    const finishKlines = () => {
      if (totalKlines.length === 0) {
        onHistoryCallback([], { noData: true });
      } else {
        let historyCBArray = totalKlines.map((kline) => ({
          time: Number(kline[0]),
          open: Number(kline[1]),
          high: Number(kline[2]),
          low: Number(kline[3]),
          close: Number(kline[4]),
          volume: Number(kline[5]),
        }));
        onHistoryCallback(historyCBArray, { noData: false });
      }
    };

    const getKlines = async (from: number, to: number) => {
      try {
        let test = from;
        let test2 = to;
        const data = await this.bybitKlines(symbolInfo.name, interval, from, to, kLinesLimit);
        totalKlines = totalKlines.concat(data);
        totalKlines = totalKlines.sort((a, b) => Number(a[0]) - Number(b[0]));
        finishKlines();
      } catch (e) {
        console.error(e);
        onErrorCallback(`Error in 'getKlines' func`);
      }
    };
    return getKlines(from, to);
  }

  subscribeBars(
    symbolInfo: any,
    resolution: string,
    onRealtimeCallback: (data: any) => void,
    subscriberUID: string,
    onResetCacheNeededCallback?: () => void
  ): void {
    this.ws.subscribeOnStream(
      symbolInfo,
      resolution,
      onRealtimeCallback,
      subscriberUID,
      onResetCacheNeededCallback
    );
  }

  unsubscribeBars(subscriberUID: string): void {
    this.ws.unsubscribeFromStream(subscriberUID);
  }
}
